import React, { useContext, useState, useMemo, useEffect } from 'react';
import { PlanningContext } from '../context';
import { Body2, GridItemRow, ModalActions, ModalContent, ModalTitle, PrimaryButton, SecondaryButton } from '../blocks';
import { MODALS, TEMP_STOP } from '../constants';
import { useMutation } from '@apollo/client';
import { UPDATE_STOP_SCALARS } from '../graphql/mutations';
import { buildStopScalarsOptimistic } from '../graphql/updateStopsOptimistic';
import FTLStopHelpers from '@/utilities/FTLStopHelpers';
import * as Sentry from '@sentry/react';
import { cloneDeep } from 'lodash';
import NavResponsiveModal from '@/components/Navigation/NavResponsiveModal';
import { useClientUser } from '@/hooks';
import StartTimeSelect from '../../misc/StartTimeSelect';
import zipcode_to_timezone from 'zipcode-to-timezone';
import { genAttributes } from '@onward-delivery/core';
import { DISPATCH_PLAN_SERIALIZATION_KEY } from '@/graphql/serializingLink';
import { getDispatchSettingPreference, windowEndExceedsCeiling } from '@/utilities/calculateTimeframes';
import { toMinuteIso } from '@/utilities/toMinuteIso';
const CustomTimeframeModal = () => {
    const {
        actionableRoute,
        setActionableRoute,
        actionableStop,
        setActionableStop,
        modalOpen,
        setModalOpen,
        setError,
        deliveryDate,
    } = useContext(PlanningContext);
    const { user_id, dispatch_settings } = useClientUser();
    const [startDelWindow, setStartDelWindow] = useState();
    const [endDelWindow, setEndDelWindow] = useState();
    const [timeError, setTimeError] = useState(null);

    useEffect(() => {
        setStartDelWindow(actionableStop?.del_window_start);
        setEndDelWindow(actionableStop?.del_window_end);
    }, [actionableStop]);

    const routeTimeZone = useMemo(() => {
        const order = actionableRoute?.orders?.[0]?.order || {};
        const { zip } = genAttributes(order);
        return zipcode_to_timezone.lookup(order[zip]) || Intl.DateTimeFormat().resolvedOptions().timeZone;
    }, [actionableRoute]);

    const [updateStopScalars] = useMutation(UPDATE_STOP_SCALARS, {
        context: { serializationKey: DISPATCH_PLAN_SERIALIZATION_KEY },
    });

    // Warn-only: manual edits may push a window past the ceiling, but the user is allowed to proceed.
    const endExceedsCeiling = useMemo(() => {
        const firstOrder = actionableStop?.stopOrders?.[0];
        if (!firstOrder || !endDelWindow) return false;
        const ceiling = getDispatchSettingPreference(
            'preferences_delivery_window_ceiling',
            dispatch_settings,
            firstOrder.shipper_id
        );
        return windowEndExceedsCeiling(endDelWindow, firstOrder.dropoff_zip, ceiling);
    }, [actionableStop, endDelWindow, dispatch_settings]);

    const handleModalClose = () => {
        setModalOpen(null);
        setActionableRoute(null);
        setActionableStop(null);
        setTimeError(null);
    };
    const handleTimeFrameSelect = (e, type) => {
        let date = toMinuteIso(new Date(e.target.value));
        if (type === 'start') {
            if (date > endDelWindow) {
                setTimeError('Start time must be before end time');
                return;
            }
            setStartDelWindow(date);
        } else {
            if (date < startDelWindow) {
                setTimeError('End time must be after start time');
                return;
            }
            setEndDelWindow(date);
        }
        setTimeError(null);
    };

    const handleTimeframeUpdate = () => {
        let newStops = cloneDeep(actionableRoute?.stopsByRouteId || []);
        if (actionableStop.del_window_end === endDelWindow && actionableStop.del_window_start === startDelWindow)
            return;
        const updatedStops = newStops.map((stop) =>
            stop.stop_id === actionableStop.stop_id
                ? { ...stop, del_window_start: startDelWindow, del_window_end: endDelWindow }
                : stop
        );

        const optimisticStops = updatedStops.map((stop) => ({ ...TEMP_STOP, ...stop, __typename: 'stops' }));
        const optimisticOrders = (actionableStop?.stopOrders || []).map((order) => ({
            ...order,
            del_window_start: startDelWindow,
            del_window_end: endDelWindow,
            __typename: 'orders',
        }));

        updateStopScalars({
            variables: {
                route_id: actionableRoute?.route_id,
                route_update: {},
                order_updates:
                    actionableStop?.stopOrders?.map((order) => ({
                        where: { order_id: { _eq: order.order_id } },
                        _set: {
                            del_window_start: startDelWindow,
                            del_window_end: endDelWindow,
                        },
                    })) || [],
                // inserts/deletes are provably empty here (updatedStops is a
                // 1:1 map of stopsByRouteId) and the scalar doc has no roots
                // for them
                updates: FTLStopHelpers.gqlStopUpdates(updatedStops, actionableRoute).updates,
            },
            optimisticResponse: buildStopScalarsOptimistic({
                route: actionableRoute,
                routeUpdate: {},
                stopsUpdated: optimisticStops,
                ordersUpdated: optimisticOrders,
            }),
            onError: (error) => {
                setError(
                    error,
                    `Failed to update timesframes for stop(s) ${actionableStop?.ordering} from route ${actionableRoute?.route_number}`
                );
            },
        });

        handleModalClose();
    };

    return (
        <NavResponsiveModal open={modalOpen === MODALS.CUSTOM_TIMEFRAME} onClose={handleModalClose}>
            <ModalTitle>{`Customize timeframes for Stop ${actionableStop?.ordering} on Route ${actionableRoute?.route_number}`}</ModalTitle>
            <ModalContent>
                <GridItemRow>
                    <div>Start Time</div>
                    <div>
                        <StartTimeSelect
                            timeZone={routeTimeZone}
                            value={startDelWindow ? new Date(startDelWindow).getTime() : 0}
                            onChange={(e) => handleTimeFrameSelect(e, 'start')}
                            label="Start Time"
                            deliveryDate={deliveryDate}
                            interval={15}
                        />
                    </div>
                </GridItemRow>
                <GridItemRow>
                    <div>End Time</div>
                    <div>
                        <StartTimeSelect
                            timeZone={routeTimeZone}
                            value={endDelWindow ? new Date(endDelWindow).getTime() : 0}
                            onChange={(e) => handleTimeFrameSelect(e, 'end')}
                            label="End Time"
                            deliveryDate={deliveryDate}
                            interval={15}
                        />
                    </div>
                </GridItemRow>
                {timeError && (
                    <GridItemRow>
                        <Body2 style={{ color: 'red', marginTop: '10px', marginLeft: '10%', alignContent: 'center' }}>
                            Error: {timeError}
                        </Body2>
                    </GridItemRow>
                )}
                {!timeError && endExceedsCeiling && (
                    <GridItemRow>
                        <Body2
                            style={{ color: '#B45309', marginTop: '10px', marginLeft: '10%', alignContent: 'center' }}
                        >
                            Warning: this end time is past your configured delivery window ceiling. You can still save
                            it, but automated routing won't produce windows this late.
                        </Body2>
                    </GridItemRow>
                )}
            </ModalContent>
            <ModalActions>
                <SecondaryButton onClick={handleModalClose}>Cancel</SecondaryButton>
                <PrimaryButton disabled={timeError} onClick={handleTimeframeUpdate}>
                    Confirm
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default CustomTimeframeModal;
